import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Flex,
  FlexProps,
  FormLabel,
  Icon,
  BoxProps,
  Tooltip,
} from "@chakra-ui/react";
import {
  CreatableSelect,
  MultiValue,
  ChakraStylesConfig,
  Select,
} from "chakra-react-select";
import { FiChevronDown, FiSearch } from "react-icons/fi";
import { FaInfoCircle } from "react-icons/fa";
import { motion, Transition } from "framer-motion";

const MotionFlex = motion<FlexProps>(Flex);
const MotionLabel = motion(FormLabel);
const MotionBox = motion<BoxProps>(Box);

export type Option = { label: string; value: string; isFixed?: boolean };

type KYDMultiSelectProps = {
  values: Option[];
  onChange?: (values: Option[]) => void;
  onAdd?: (value: Option) => void;
  onRemove?: (value: Option) => void;
  options?: Option[];
  label?: string;
  placeholder?: string;
  createable?: boolean;
  onInputChange?: (input: string) => void;
  id?: string;
  height?: string;
  minHeight?: string;
  maxAllowed?: number;
  isDisabled?: boolean;
  icon?: React.ElementType;
  variant?: "default" | "labeled";
  tooltip?: string;
};

const chakraStyles: ChakraStylesConfig<Option, true> = {
  container: (p) => ({ ...p, width: "100%", padding: 0 }),
  control: (p, state) => ({
    ...p,
    padding: 0,
    minHeight: "32px",
    height: "auto",
    border: "none",
    boxShadow: "none",
    outline: "none !important",
    ":focus-visible": {
      border: "none",
      boxShadow: "none",
    },
    "&[data-focus-visible]": {
      border: "none",
      boxShadow: "none",
    },
  }),
  valueContainer: (p) => ({
    ...p,
    padding: "2px 4px",
    paddingLeft: 0,
    flexWrap: "wrap",
    gap: "4px",
  }),
  dropdownIndicator: () => ({ display: "none" }),
  indicatorSeparator: () => ({ display: "none" }),
  placeholder: (p) => ({
    ...p,
    margin: 0,
    padding: 0,
    color: "#00000080",
    paddingLeft: "2px",
    fontSize: "14px",
    fontFamily: "Helvetica",
  }),
  input: (p) => ({ ...p, margin: 0, padding: 0 }),
  multiValue: (p) => ({
    ...p,
    bg: "#EDF2F7",
    borderRadius: "4px",
    padding: "0 4px",
    margin: 0,
    marginRight: "4px",
    marginBottom: "4px",
  }),
  multiValueLabel: (p) => ({
    ...p,
    color: "#1A202C",
    fontSize: "14px",
    padding: "2px 4px",
  }),
  multiValueRemove: (p, state) =>
    state.data.isFixed
      ? { display: "none" }
      : {
          ...p,
          ":hover": { bg: "#E2E8F0", color: "#4A5568" },
        },
  menu: (p) => ({ ...p, zIndex: 9999 }),
};

export default function KYDMultiSelect({
  values,
  onChange,
  options,
  label,
  placeholder,
  createable,
  onInputChange,
  onAdd,
  onRemove,
  isDisabled,
  icon = FiSearch,
  id,
  height,
  minHeight = "48px",
  maxAllowed = 9999,
  variant = "default",
  tooltip,
}: KYDMultiSelectProps) {
  const [focused, setFocused] = useState(false);
  const hasValue = values.length > 0;
  const hasRemovableValue = values.some((value) => !value.isFixed);
  const active = (focused || hasValue) && label;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const slide: Transition = { duration: 0.1, ease: "easeInOut" };

  const Component = useMemo(
    () => (createable ? CreatableSelect : Select),
    [createable]
  );

  const optionsFixed = useMemo(
    () => (createable ? (options?.length ? options : []) : options),
    [createable, options]
  );

  const handleFocus = useCallback(() => setFocused(true), []);
  const handleBlur = useCallback(() => setFocused(false), []);

  const handleInputChange = useCallback(
    (input: string) => {
      if (maxAllowed && values.length >= maxAllowed) {
        return;
      }
      onInputChange?.(input);
    },
    [onInputChange]
  );

  const handleAdd = useCallback(
    (input: Option) => {
      onAdd?.(input);
    },
    [onAdd]
  );

  const handleChange = useCallback(
    (newVals: MultiValue<Option>) => {
      const fixedValues = values.filter((value) => value.isFixed);
      const nextValues = [
        ...fixedValues,
        ...newVals.filter(
          (value) =>
            !fixedValues.some((fixedValue) => fixedValue.value === value.value)
        ),
      ];
      onChange?.(
        nextValues.map((v) => ({
          label: v.label,
          value: v.value,
          isFixed: v.isFixed,
        }))
      );
      const isAdded = newVals.length > values.length;
      if (isAdded) {
        handleAdd(newVals[newVals.length - 1]);
      }
      const isRemoved = nextValues.length < values.length;
      if (isRemoved) {
        const removedValue = values.find(
          (v) => !nextValues.some((nv) => nv.value === v.value)
        );
        onRemove?.(removedValue);
      }
    },
    [onChange, handleAdd, onRemove, values]
  );

  // Shared select component
  const selectComponent = (
    <Component
      onMenuOpen={() => {
        const duration = variant === "default" && active ? 100 : 0;
        setTimeout(() => setIsMenuOpen(true), duration);
      }}
      isDisabled={isDisabled}
      onMenuClose={() => setIsMenuOpen(false)}
      inputId={`kyd-multiselect-${id}`}
      key={`kyd-multiselect-component-input-${id}`}
      id={`kyd-multiselect-component-input-${id}`}
      isMulti
      menuIsOpen={
        options ? (maxAllowed <= values.length ? false : isMenuOpen) : false
      }
      options={optionsFixed}
      value={values}
      isClearable={hasRemovableValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      placeholder={placeholder}
      chakraStyles={chakraStyles}
      onFocus={handleFocus}
      onBlur={handleBlur}
      {...(createable && {
        onCreateOption: (input) => {
          const newValue = { label: input, value: input };
          onChange([...values, newValue]);
        },
      })}
    />
  );

  // Shared label with tooltip
  const labelWithTooltip = label && (
    <>
      <FormLabel
        htmlFor={id}
        fontSize={"16px"}
        fontWeight={"500"}
        fontFamily={"Work Sans"}
        lineHeight={"24px"}
        color="#1A202C"
        pointerEvents="none"
        display="inline"
        margin={0}
      >
        {label}
      </FormLabel>
      {tooltip && (
        <Tooltip
          label={tooltip}
          fontSize="12px"
          placement="bottom-start"
          hasArrow
          bg="gray.700"
          color="white"
          px={2}
          py={1}
          borderRadius="md"
        >
          <span
            style={{
              display: "inline-block",
              alignItems: "center",
            }}
          >
            <Icon
              as={FaInfoCircle}
              color="white"
              background="gray.500"
              borderRadius="full"
              marginLeft={2}
              cursor="pointer"
              verticalAlign="middle"
            />
          </span>
        </Tooltip>
      )}
    </>
  );

  if (variant === "labeled") {
    return (
      <Box display="flex" flexDirection="column" gap={2} width="100%">
        {labelWithTooltip && <Box>{labelWithTooltip}</Box>}
        <Box
          flex="1"
          display="flex"
          alignItems="flex-start"
          minHeight="54px"
          height="auto"
          padding={"8px 17px"}
          border={"1px solid"}
          borderColor={"gray.300"}
          borderRadius="8px"
          bg="white"
          position="relative"
        >
          <Icon
            as={FiChevronDown}
            position="absolute"
            right="12px"
            top="16px"
            color="black"
          />
          {selectComponent}
        </Box>
      </Box>
    );
  }

  // Default variant
  return (
    <Box
      position="relative"
      width="100%"
      border="1px solid #E6E6E6"
      borderRadius="8px"
      bg="white"
      boxShadow="0px 10px 15px 0px #F7F7F7"
      p={3}
      minHeight={minHeight}
      height={height || "auto"}
      key={`kyd-multiselect-box-${id}`}
    >
      <Icon
        as={icon}
        position="absolute"
        left="12px"
        top={active ? "16px" : "50%"}
        transform={active ? "none" : "translateY(-50%)"}
        color="rgba(150,150,150,0.7)"
        zIndex={2}
      />

      <MotionFlex
        layout
        minHeight={minHeight}
        height={height || "auto"}
        transition={slide as any}
        direction={active ? "column" : "row"}
        align={active ? "stretch" : "center"}
        gap={active ? 0 : 2}
        pl="36px"
        pt={active ? "8px" : 0}
        pb={active ? "4px" : 0}
        id={`kyd-multiselect-flex-${id}`}
        key={`kyd-multiselect-flex-${id}`}
      >
        {label && (
          <MotionBox layout="position" transition={slide as any}>
            <MotionLabel
              id={`kyd-multiselect-label-${id}`}
              fontSize={active ? "12px" : "14px"}
              fontWeight={active ? "400" : "500"}
              color="#00000080"
              whiteSpace={active ? "normal" : "nowrap"}
              mb={0}
              mr={1}
              pointerEvents="none"
              display="flex"
              alignItems="center"
              key={`kyd-multiselect-label-${id}`}
            >
              {label}
            </MotionLabel>
          </MotionBox>
        )}

        <MotionBox
          layoutId={`kyd-multiselect-box-${id}`}
          key={`kyd-multiselect-box-${id}`}
          width="100%"
          layout="position"
          transition={slide as any}
        >
          {selectComponent}
        </MotionBox>
      </MotionFlex>
    </Box>
  );
}
